import { useEffect, useMemo, useState } from 'react'
import { paymentsApi } from '../../../services/api'
import Button from '../../../components/ui/Button'
import Card, { CardBody, CardHeader } from '../../../components/ui/Card'
import { Field, Input, Select } from '../../../components/ui/Field'

const statusMetaMap = {
  successful: {
    label: 'Successful',
    pillClassName: 'bg-emerald-50 text-emerald-800 border-emerald-200'
  },
  pending: {
    label: 'Pending',
    pillClassName: 'bg-amber-50 text-amber-800 border-amber-200'
  },
  failed: {
    label: 'Failed',
    pillClassName: 'bg-rose-50 text-rose-800 border-rose-200'
  },
  abandoned: {
    label: 'Abandoned',
    pillClassName: 'bg-slate-50 text-slate-700 border-slate-200'
  }
}

const StatusPill = ({ status }) => {
  const meta = statusMetaMap[status] || { label: String(status || 'unknown'), pillClassName: 'bg-slate-50 text-slate-700 border-slate-200' }

  return (
    <span
      className={[
        'inline-flex items-center rounded-full border px-3 py-1',
        'text-xs font-semibold tracking-wide',
        meta.pillClassName
      ].join(' ')}
    >
      {meta.label}
    </span>
  )
}

const formatAmount = (amount, currency = 'NGN') => {
  const value = Number(amount || 0)
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value)
}

const formatDate = (payment) => {
  const value = payment?.date || payment?.createdAt
  if (!value) return '-'
  const dt = new Date(value)
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleString()
}

const PRODUCT_TYPE_LABELS = {
  DOCUMENTATION_GENERATION: 'Project documentation',
  PROPOSAL_GENERATION: 'Project proposal',
  SIWES_REPORT_GENERATION: 'SIWES report',
  IDEA_GENERATION_PACK: 'Idea generation pack',
  EXTRA_EDITS: 'Extra edits'
}

const getPaymentPurpose = (payment) =>
  PRODUCT_TYPE_LABELS[payment?.productType] || payment?.productType || '-'

const getPaymentTopic = (payment) => {
  const topicTitle = payment?.project?.topic?.title
  if (topicTitle) return topicTitle
  if (payment?.project?.title) return payment.project.title
  if (payment?.documentation?.title) return payment.documentation.title
  return '-'
}

const Payments = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 })

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchTerm)
    }, 350)

    return () => clearTimeout(timeout)
  }, [searchTerm])

  useEffect(() => {
    let active = true
    const fetchPayments = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await paymentsApi.mine({
          page,
          limit,
          paymentStatus: status || undefined,
          q: search.trim() || undefined
        })
        if (!active) return
        setItems(res?.data?.data || [])
        setPagination(res?.data?.pagination || { total: 0, page: 1, limit, pages: 1 })
      } catch (err) {
        if (!active) return
        setItems([])
        setError(err?.response?.data?.message || 'Could not load payments')
      } finally {
        if (active) setLoading(false)
      }
    }

    void fetchPayments()
    return () => {
      active = false
    }
  }, [page, limit, status, search])

  const isEmpty = useMemo(() => !loading && !error && items.length === 0, [items, loading, error])

  const paginationPages = pagination.pages || 1
  const total = pagination.total || 0
  const from = total ? (pagination.page - 1) * pagination.limit + 1 : 0
  const to = total ? Math.min(total, from + items.length - 1) : 0
  const hasFilters = Boolean(searchTerm.trim() || search.trim() || status)

  return (
    <section className="space-y-5">
      <Card className="shadow-soft overflow-hidden">
        <CardHeader
          title="Payments"
          subtitle="Track your payment history. Search by transaction ref, purpose, or topic."
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPage(1)
                setSearch('')
                setSearchTerm('')
                setStatus('')
              }}
              disabled={loading || (!hasFilters && page === 1)}
            >
              Clear
            </Button>
          }
          className="bg-gradient-to-r from-emerald-500/10 via-white to-white border-b border-slate-200/70"
        />
        <CardBody className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Field
              label="Search"
              hint="tx ref, purpose, or topic"
              className="lg:col-span-1"
            >
              <div className="relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                  />
                </svg>
                <Input
                  type="text"
                  placeholder="Search by tx ref, purpose, or topic"
                  value={searchTerm}
                  onChange={(e) => {
                    setPage(1)
                    setSearchTerm(e.target.value)
                  }}
                  className="pl-10"
                />
              </div>
            </Field>

            <Field label="Status" hint="Payment state" className="lg:col-span-1">
              <Select
                value={status}
                onChange={(e) => {
                  setPage(1)
                  setStatus(e.target.value)
                }}
              >
                <option value="">All statuses</option>
                <option value="successful">Successful</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="abandoned">Abandoned</option>
              </Select>
            </Field>

            <Field label="Per page" hint="Results per request" className="lg:col-span-1">
              <Select
                value={limit}
                onChange={(e) => {
                  setPage(1)
                  setLimit(Number(e.target.value))
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </Select>
            </Field>
          </div>

          {error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
              <div className="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 8a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="flex justify-center py-10">
              <span className="loading loading-spinner loading-md" />
            </div>
          ) : null}

          {isEmpty ? (
            <div className="flex flex-col items-center justify-center text-center border border-dashed border-slate-300/80 rounded-2xl p-10 bg-slate-50">
              <div className="h-12 w-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c1.657 0 3 .895 3 2s-1.343 2-3 2-3 .895-3 2 1.343 2 3 2m0-10V6m0 14v-2" />
                </svg>
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-900">No payments found yet</p>
              <p className="mt-1 text-sm text-slate-600">When you complete a project payment, it will show up here.</p>
            </div>
          ) : null}

          {!loading && !isEmpty ? (
            <div className="space-y-4">
              <div className="hidden md:block overflow-hidden rounded-2xl border border-slate-200/70">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-semibold text-slate-600">
                      <th className="px-5 py-3">Purpose</th>
                      <th className="px-5 py-3">Topic</th>
                      <th className="px-5 py-3">Amount</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((payment) => (
                      <tr
                        key={payment._id}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-5 py-3 font-medium text-slate-900">
                          {getPaymentPurpose(payment)}
                        </td>
                        <td className="px-5 py-3 text-slate-700">
                          {getPaymentTopic(payment)}
                        </td>
                        <td className="px-5 py-3 text-slate-900 font-semibold">
                          {formatAmount(payment?.amount, payment?.currency || 'NGN')}
                        </td>
                        <td className="px-5 py-3">
                          <StatusPill status={payment?.paymentStatus} />
                        </td>
                        <td className="px-5 py-3 text-slate-600">{formatDate(payment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {items.map((payment) => (
                  <article
                    key={payment._id}
                    className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {getPaymentPurpose(payment)}
                        </h3>
                        <p className="text-sm text-slate-700 mt-0.5 truncate">{getPaymentTopic(payment)}</p>
                        <p className="text-xs text-slate-500 mt-1">{formatDate(payment)}</p>
                      </div>
                      <StatusPill status={payment?.paymentStatus} />
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-sm text-slate-600">Amount</p>
                      <p className="text-sm font-bold text-slate-900">
                        {formatAmount(payment?.amount, payment?.currency || 'NGN')}
                      </p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between rounded-2xl border border-slate-200/70 bg-white p-4">
                <p className="text-sm text-slate-600">
                  Page {pagination.page} of {paginationPages}
                  {total ? (
                    <span>
                      {' '}
                      · Showing {from}-{to} of {total}
                    </span>
                  ) : null}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= paginationPages}
                    onClick={() => setPage((prev) => Math.min(prev + 1, paginationPages))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </section>
  )
}

export default Payments
